import type {
  DesignLabFormType,
  CompositionForm,
  DesignProjectResponse
} from "@design-lab/shared/types";
import type { CompositionQuantities, VariantQuantities } from "@design-lab/entities/composition";
import type { printOnDemand } from "common/utils/models";

// Using the type directly to avoid extension issues
type Composition = printOnDemand.getProduct.Composition;

// Helper function to transform flat variants to nested structure with quantity objects
function transformVariantsToQuantities(variants: CompositionForm["variants"]): VariantQuantities {
  return Object.entries(variants).reduce<VariantQuantities>(
    (acc, [variantId, quantity]) => ({
      ...acc,
      [variantId]: { quantity }
    }),
    {}
  );
}

// Helper function to transform nested variant structure to flat structure
function transformQuantitiesToVariants(
  variantsData: VariantQuantities
): CompositionForm["variants"] {
  return Object.entries(variantsData).reduce<CompositionForm["variants"]>(
    (acc, [variantId, variantData]) => {
      if (typeof variantData === "object" && variantData !== null && "quantity" in variantData) {
        acc[variantId] = (variantData as { quantity: number }).quantity;
      }
      return acc;
    },
    {}
  );
}

// Helper function to build quantities object
export function buildQuantities(
  compositions: DesignLabFormType["compositions"]["addedCompositions"]
): CompositionQuantities {
  return compositions.reduce<CompositionQuantities>(
    (quantities, comp) => ({
      ...quantities,
      [comp.id]: transformVariantsToQuantities(comp.variants)
    }),
    {}
  );
}

// Helper function to build quantities object for summary
export function buildQuantitiesForSummary(
  composition: Composition,
  addedCompositions: DesignLabFormType["compositions"]["addedCompositions"]
): Record<string, number> {
  const added = addedCompositions.find(c => c.id === composition.id);
  if (!added) return {};

  const result: Record<string, number> = {};

  for (const variant of composition.variants) {
    const qty = added.variants[variant.id];
    if (qty) {
      result[variant.size] = qty;
    }
  }

  return result;
}

// Helper function to build compositions data from quantities
export function buildCompositionsData(
  quantities: DesignProjectResponse["quantities"],
  activeCompositionId: number
): DesignLabFormType["compositions"] {
  const addedCompositions = Object.entries(quantities).flatMap(([catalogId, compositions]) =>
    Object.entries(compositions).map(([compositionId, variantsData]) => ({
      id: compositionId,
      catalogId,
      colorName: "", // This will need to be filled from product data
      variants: transformQuantitiesToVariants(variantsData)
    }))
  );

  return {
    currentCompositionId: String(activeCompositionId),
    addedCompositions
  };
}
